import React, {FC, useEffect, useState, useRef} from 'react';
import {useQuery} from '@tanstack/react-query';
import {MdSearch} from 'react-icons/md';
import {createSearchParams, useNavigate} from 'react-router-dom';
import LoadingModal from '../../../components/LoadingModal';
import useMe from '../../../hooks/useMe';
import CommonCareSearches from './CommonCareSearches';
import MedicalEmergencyModal from './MedicalEmergencyModal';
import {
	Badge,
	Box,
	Button,
	Flex,
	FormControl,
	Heading,
	Input,
	Stack,
	Text,
	useDisclosure,
} from 'components/ui/chakra';
import CareAutocompleteController from 'puddleglum/api/patient/CareAutocompleteController';

type AutocompleteItem = {
	id: number;
	name: string;
	type: string;
};

type ProcedureAutocompleteItem = AutocompleteItem & {
	type: 'treatments' | 'treatment_groups';
};

const TYPE_LABELS: Record<string, {label: string; colorScheme: string}> = {
	providers: {label: 'Provider', colorScheme: 'blue'},
	specialties: {label: 'Specialty', colorScheme: 'orange'},
	organizations: {label: 'Clinic', colorScheme: 'purple'},
	treatments: {label: 'Procedure', colorScheme: 'green'},
	treatment_groups: {label: 'Procedure Group', colorScheme: 'teal'},
};

const PLACEHOLDERS = [
	'e.g., Cardiologist',
	'e.g., Family physician',
	'e.g., Pediatrician',
	'e.g., Urgent care',
	'e.g., Lipid panel',
	'e.g., Mammogram',
	'e.g., Colonoscopy',
	'e.g., CT scan of my neck',
];

const isProcedureAutocompleteItem = (item: AutocompleteItem): item is ProcedureAutocompleteItem =>
	item.type === 'treatments' || item.type === 'treatment_groups';

const normalizeSearchValue = (value: string) => value.trim().toLowerCase();

const getAutocompleteCode = (value: string) => {
	const match = value.match(/^\(([^)]+)\)/);
	return match ? normalizeSearchValue(match[1]) : null;
};

const stripAutocompleteCode = (value: string) => value.replace(/^\([^)]+\)\s*/, '');

const getAutocompleteMatchScore = (item: AutocompleteItem, search: string) => {
	const normalizedSearch = normalizeSearchValue(search);
	const normalizedName = normalizeSearchValue(item.name);
	const normalizedNameWithoutCode = normalizeSearchValue(stripAutocompleteCode(item.name));
	const normalizedCode = getAutocompleteCode(item.name);

	if (!normalizedSearch) return null;

	if (
		normalizedCode === normalizedSearch ||
		normalizedName === normalizedSearch ||
		normalizedNameWithoutCode === normalizedSearch
	) {
		return 0;
	}

	if (
		normalizedCode?.startsWith(normalizedSearch) ||
		normalizedName.startsWith(`${normalizedSearch} (`) ||
		normalizedNameWithoutCode.startsWith(`${normalizedSearch} (`)
	) {
		return 1;
	}

	if (
		normalizedName.startsWith(normalizedSearch) ||
		normalizedNameWithoutCode.startsWith(normalizedSearch)
	) {
		return 2;
	}

	if (
		normalizedName.includes(normalizedSearch) ||
		normalizedNameWithoutCode.includes(normalizedSearch)
	) {
		return 3;
	}

	return null;
};

interface MedicalCareSearchProps {}

const MedicalCareSearch: FC<MedicalCareSearchProps> = () => {
	const passedInShoppingItem = localStorage.getItem('allora_shopping_item');

	const {me} = useMe();
	const {
		isOpen: isMedicalEmergencyModalOpen,
		onOpen: onMedicalEmergencyModalOpen,
		onClose: onMedicalEmergencyModalClose,
	} = useDisclosure();
	const {
		isOpen: isLoadingModalOpen,
		onOpen: onLoadingModalOpen,
		onClose: onLoadingModalClose,
	} = useDisclosure();

	const [selectedProcedureId, setSelectedProcedureId] = useState<number>();
	const [typeOfSelectedResult, setTypeOfSelectedResult] = useState<string>();
	const [placeholder, setPlaceholder] = useState(PLACEHOLDERS[0]);
	const [searchText, setSearchText] = useState('');
	const [isFocused, setIsFocused] = useState(false);
	const [shouldResolveCommonProcedureSearch, setShouldResolveCommonProcedureSearch] =
		useState(false);

	const navigate = useNavigate();

	const searchButtonRef = useRef<HTMLButtonElement>(null);

	const {data: autocompleteData} = useQuery(
		['autocomplete-care', searchText],
		async () => {
			const reply = await CareAutocompleteController.index({search: searchText});
			return reply.data;
		},
		{
			enabled: searchText.length > 2,
		},
	);

	useEffect(() => {
		const updatePlaceholder = () => {
			setPlaceholder(PLACEHOLDERS[Math.floor(Math.random() * PLACEHOLDERS.length)]);
		};

		const interval = setInterval(updatePlaceholder, 5000);
		return () => clearInterval(interval);
	}, []);

	const resolveProcedureAutocompleteItem = async (
		selection: string,
	): Promise<ProcedureAutocompleteItem | null> => {
		try {
			const reply = await CareAutocompleteController.index({search: selection});
			const candidates = (reply.data ?? [])
				.map((item, index) => ({
					index,
					item,
					score: getAutocompleteMatchScore(item, selection),
				}))
				.filter(
					(
						candidate,
					): candidate is {index: number; item: AutocompleteItem; score: number} =>
						candidate.score !== null,
				)
				.sort((a, b) => {
					if (a.score !== b.score) return a.score - b.score;

					return a.index - b.index;
				});

			const bestMatch = candidates[0]?.item;

			return bestMatch && isProcedureAutocompleteItem(bestMatch) ? bestMatch : null;
		} catch {
			return null;
		}
	};

	const performSearch = async () => {
		if (
			selectedProcedureId &&
			(typeOfSelectedResult === 'treatments' || typeOfSelectedResult === 'treatment_groups')
		) {
			navigate(
				`/procedure-search-results?id=${selectedProcedureId}&type=${typeOfSelectedResult}`,
			);
			return;
		}

		if (shouldResolveCommonProcedureSearch) {
			const procedureItem = await resolveProcedureAutocompleteItem(searchText);

			if (procedureItem) {
				navigate(
					`/procedure-search-results?id=${procedureItem.id}&type=${procedureItem.type}`,
				);
				return;
			}
		}

		navigate({
			pathname: '/provider-search-results',
			search: createSearchParams({search: searchText}).toString(),
		});
	};

	const handleSearchButtonClick = () => {
		onMedicalEmergencyModalOpen();
	};

	const handleNotEmergencyClick = async () => {
		onMedicalEmergencyModalClose();
		onLoadingModalOpen();
		await performSearch();
	};

	const handleSuggestionClick = (item: AutocompleteItem) => {
		setIsFocused(false);
		setSearchText(item.name);
		setTypeOfSelectedResult(item.type);
		setShouldResolveCommonProcedureSearch(false);
		if (item.type === 'treatments' || item.type === 'treatment_groups') {
			setSelectedProcedureId(item.id);
		} else {
			setSelectedProcedureId(undefined);
		}
		searchButtonRef.current?.click();
	};

	const handleCommonSearchClick = (selection: string) => {
		setSearchText(selection);
		setSelectedProcedureId(undefined);
		setTypeOfSelectedResult(undefined);
		setShouldResolveCommonProcedureSearch(true);
		onMedicalEmergencyModalOpen();
	};

	useEffect(() => {
		if (passedInShoppingItem) {
			const [type, id, locationId, organizationId, newTreatmentGroupId] =
				passedInShoppingItem.split('|');

			localStorage.removeItem('allora_shopping_item');

			if (type === 'provider') {
				navigate('/booking-request', {
					state: {
						reason: 'New booking request',
						treatment_group_ids: [newTreatmentGroupId],
						organization_id: organizationId,
						location_id: locationId,
						provider_id: id,
					},
				});
			} else if (type === 'treatment') {
				navigate('/booking-request', {
					state: {
						reason: 'New booking request',
						treatment_ids: [id],
						organization_id: organizationId,
						location_id: locationId,
						is_order_required: false,
						comments: '',
					},
				});
			} else if (type === 'treatment_group') {
				navigate('/booking-request', {
					state: {
						reason: 'New booking request',
						treatment_group_ids: [id],
						organization_id: organizationId,
						location_id: locationId,
						is_order_required: false,
						comments: '',
					},
				});
			}
		}
	}, [navigate, passedInShoppingItem]);

	if (!me || me.inactivated_at) return null;

	return (
		<Box w='full'>
			<Box
				position='relative'
				border='1px solid'
				borderColor='gray.200'
				borderRadius='2xl'
				bgGradient='linear(to-br, white, gray.50)'
				p={{base: 4, md: 6}}
				overflow='visible'
			>
				<Box
					position='absolute'
					inset={0}
					borderRadius='2xl'
					overflow='hidden'
					pointerEvents='none'
				>
					<Box
						position='absolute'
						right='-3rem'
						top='-3rem'
						w='10rem'
						h='10rem'
						borderRadius='full'
						bg='blue.50'
					/>
				</Box>

				<Stack position='relative' spacing={5}>
					<Stack spacing={1}>
						<Text
							color='gray.500'
							fontSize='xs'
							fontWeight='semibold'
							textTransform='uppercase'
							letterSpacing='0.08em'
						>
							Search Care
						</Text>
						<Heading size='md' color='gray.700'>
							Find care faster
						</Heading>
						<Text color='gray.600' fontSize='sm'>
							Search providers, specialties, and procedures together in one place.
						</Text>
					</Stack>

					<Stack direction={{base: 'column', md: 'row'}} spacing={3}>
						<FormControl position='relative'>
							<Input
								type='search'
								name='care-search'
								autoComplete='off'
								size={{base: 'lg', md: 'md'}}
								h='3rem'
								borderRadius='xl'
								px={5}
								bgColor='white'
								borderColor='gray.300'
								placeholder={placeholder}
								value={searchText}
								onFocus={() => setIsFocused(true)}
								onBlur={() => {
									setTimeout(() => setIsFocused(false), 150);
								}}
								onKeyDown={(event) => {
									if (event.key !== 'Enter' || !searchText) return;
									event.preventDefault();
									handleSearchButtonClick();
								}}
								onChange={(e) => {
									setSearchText(e.target.value);
									setSelectedProcedureId(undefined);
									setTypeOfSelectedResult(undefined);
									setShouldResolveCommonProcedureSearch(false);
								}}
							/>
							{isFocused && autocompleteData && autocompleteData.length > 0 && (
								<Box
									position='absolute'
									top='100%'
									left={0}
									right={0}
									bg='white'
									borderWidth='1px'
									borderColor='gray.200'
									borderRadius='xl'
									boxShadow='lg'
									mt={2}
									zIndex={10}
									maxH='400px'
									overflowY='auto'
								>
									{autocompleteData.map((item) => {
										const meta = TYPE_LABELS[item.type] ?? {
											label: item.type,
											colorScheme: 'gray',
										};
										return (
											<Flex
												key={`${item.type}-${item.id}`}
												px={3}
												py={2.5}
												align='center'
												justify='space-between'
												gap={3}
												_hover={{bg: 'gray.50'}}
												cursor='pointer'
												onMouseDown={() => handleSuggestionClick(item)}
											>
												<Text flex='1' noOfLines={1}>
													{item.name}
												</Text>
												<Badge
													colorScheme={meta.colorScheme}
													flexShrink={0}
													textTransform='none'
												>
													{meta.label}
												</Badge>
											</Flex>
										);
									})}
								</Box>
							)}
						</FormControl>
						<Button
							ref={searchButtonRef}
							isDisabled={!searchText}
							leftIcon={<MdSearch />}
							size={{base: 'lg', md: 'md'}}
							h='3rem'
							w={{base: 'full', md: '11rem'}}
							colorScheme='blue'
							borderRadius='xl'
							onClick={handleSearchButtonClick}
						>
							Search
						</Button>
					</Stack>

					<CommonCareSearches onSelectSearch={handleCommonSearchClick} />
				</Stack>
			</Box>

			<MedicalEmergencyModal
				isOpen={isMedicalEmergencyModalOpen}
				onConfirm={handleNotEmergencyClick}
				onClose={onMedicalEmergencyModalClose}
			/>
			<LoadingModal
				estimatedTime={4000}
				steps={[
					'Querying the AI engine...',
					'Identifying possible results...',
					'Mapping results to organizations...',
					'Loading prices...',
					'Preparing your results...',
				]}
				onClose={onLoadingModalClose}
				isOpen={isLoadingModalOpen}
			/>
		</Box>
	);
};

export default MedicalCareSearch;
